/**
 * dealRail.ts — the deal page's right rail in words (deal detail slice 3,
 * §39.9 order 3; spec §3.5 C–D, §3.2, §4), pure so vitest pins it without a
 * DOM: the ONE day formatter the page uses, the hero's "Sourced from …"
 * phrase, the weighted value, and the Forecast card's plain-language
 * explanation. Nothing here invents a fact: each sentence is built from what
 * the deal's own read carries, and a value the maps do not know is logged and
 * left out rather than shown raw.
 */
import type { DealDetail, DealStage } from '../../types'
import type { SalesFieldHistoryEntry } from '../../../../api/types'
import { forecastCategoryLabel } from './vocab'
import { compactMoney } from '../../../../lib/money'
import { suggestionLabel, logRawCopy } from './dealCopy'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "13 Sep 2026", "1 Dec 2026" — no "Sept", no leading zero (spec §4), the
 *  same on every machine (no locale table). A DATE-ONLY value (expected
 *  close, stored as midnight UTC) is read in UTC so it never slips a day;
 *  an instant (created, a timeline event) in the viewer's own zone. */
export function formatDay(iso: string | null | undefined, zone: 'local' | 'utc' = 'local'): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return zone === 'utc'
    ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
    : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

/** The hero's meta phrase (spec §3.2: "Sourced from a call") — lowercase,
 *  with its article. A legacy or unknown source is logged and left out of
 *  the hero; the Details row still shows the stored value. */
const SOURCE_PHRASE: Record<string, string> = {
  email: 'Sourced from an email',
  call: 'Sourced from a call',
  linkedin: 'Sourced from LinkedIn',
  conversation: 'Sourced from a conversation',
  referral: 'Sourced from a referral',
  website: 'Sourced from the website',
  other: 'Sourced elsewhere',
}
export function sourcePhrase(source: string): string | null {
  if (!source) return null
  const p = SOURCE_PHRASE[source]
  if (!p) logRawCopy('deal source', source)
  return p ?? null
}

/** The deal's weighted value as the hero and the Forecast card print it
 *  (slice 8: rows 216 and 217). The FIGURE is the server's (deal.weightedPence
 *  — the fenced base-GBP value × the effective probability, floored by
 *  salesarith); the page never multiplies. Short form by R3 (compactMoney,
 *  always GBP), except that a deal WITH value whose weighted figure would
 *  print "£0" reads "under £1" (row 217 — a £1 deal at 10% weighs 10p); a
 *  deal of value 0 keeps its true "£0". null = nothing to weigh: the caller
 *  says why (no probability, or no captured GBP value). */
export function weightedLabel(d: Pick<DealDetail, 'weightedPence' | 'amountPence'>): string | null {
  if (d.weightedPence == null) return null
  const short = compactMoney(d.weightedPence, 'GBP')
  if (short === '£0' && d.amountPence > 0) return 'under £1'
  return short
}

/** Why a deal carries no weighted value — the two honest reasons. */
export function notWeightedReason(d: Pick<DealDetail, 'effectiveProbability'>): string {
  return d.effectiveProbability == null ? 'Not weighted — no probability' : 'Not weighted — no captured GBP value'
}

/** "Default for New Lead · Set your own" — the probability's source and the
 *  verb that changes it (spec §3.5 C). */
export function probabilityLine(source: DealDetail['probabilitySource'], stage: DealStage): { source: string; action: string } {
  if (source === 'deal') return { source: 'Set on this deal', action: 'Change' }
  if (source === 'tenant') return { source: `Your default for ${stage}`, action: 'Set your own' }
  return { source: `Default for ${stage}`, action: 'Set your own' }
}

/** What the effective category counts toward. Locked = won + Commit, Stretch
 *  = Locked + Best case (forecastHero.ts, as the server builds them). */
function countsToward(category: string | null, stage: DealStage): string {
  if (stage === 'Closed Won') return 'It is won, so it counts as won revenue in the period it closed.'
  if (stage === 'Closed Lost') return 'It is lost, so it counts toward no forecast figure.'
  switch (category) {
    case 'commit': return 'It counts toward Locked and Stretch when its close date falls in the period.'
    case 'best_case': return 'It counts toward Stretch, but not Locked, when its close date falls in the period.'
    case 'pipeline': return "It won't count toward Locked or Stretch until its category becomes Commit or Best case, or you override it."
    case 'omitted': return 'It counts toward no forecast figure.'
    case null: case '': return 'Nobody has called this deal yet, so it counts toward no forecast figure.'
    default:
      logRawCopy('forecast category', category)
      return 'It counts as its category says.'
  }
}

/** The ✦ explanation box (spec §3.5 C), from the deal and its own history:
 *  the effective category, who set it (an override, or a Klaxiora suggestion
 *  accepted the same day), and what it counts toward. */
export function forecastExplanation(d: DealDetail, history: readonly SalesFieldHistoryEntry[] = []): string {
  const ov = d.forecastOverride
  if (ov) {
    const own = d.forecastCategory ? forecastCategoryLabel(d.forecastCategory) : 'Uncategorised'
    return `Counted as ${forecastCategoryLabel(ov.category)} because of an override; the deal's own category, ${own}, stands underneath. ${countsToward(ov.category, d.stage)}`
  }
  const cat = d.forecastCategory
  const lead = cat ? `In ${forecastCategoryLabel(cat)}.` : 'Uncategorised.'
  // The newest category write, and a suggestion written the same day.
  const set = [...history].filter(h => h.field === 'forecast_category' && h.newValue).sort((a, b) => (a.changedAt < b.changedAt ? 1 : -1))[0]
  const sug = set && history.find(h => h.field === 'forecast_category_suggestion' && h.changedAt.slice(0, 10) === set.changedAt.slice(0, 10))
  const why = sug ? suggestionLabel(sug.newValue) : ''
  const origin = why.startsWith('Suggested by') ? ` ${why}, then accepted.` : ''
  return `${lead}${origin} ${countsToward(cat, d.stage)}`
}
